import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { getAwardTypesList } from './awardTypes';

export type AwardGrantRole = 'ADMIN' | 'MANAGER' | 'TEACHER' | 'COACH';

export interface AwardType {
  id: string;
  schoolId: string;
  name: string;
  description: string;
  picture?: string;
  isEnabled: boolean;
  _isRemoved: boolean;
  defaultHousePoints: number;
  allowedRoles: AwardGrantRole[];
  allowPeerNomination: boolean;
  allowNegative: boolean;
  isSystemHidden?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AcademicYear {
  id: string;
  name: string;
}

export interface AwardLogEntry {
  id: string;
  entityType: 'AwardEvent';
  entityId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  changes: any;
  performedBy: {
    id: string;
    displayName: string;
    email: string;
  };
  performedAt: string;
  description?: string;
}

export interface AwardEvent {
  id: string;
  awardType: {
    id: string;
    name: string;
    picture: string;
  };
  awardedAt: string;
  awardedByName: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  updatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  houseId?: string;
  houseName?: string;
  studentId?: string;
  student?: {
    id: string;
    firstName: string;
    lastName: string;
    displayName: string;
  };
  teamId?: string;
  eventId?: string;
  reason?: string;
  points: number;
  albumId?: string | null;
}

export interface AwardEventInput {
  awardTypeId: string;
  awardedAt: string;
  awardedByName?: string;
  points: number;
  reason?: string;
  studentData: Array<{
    userId: string;
    permissionId: string;
  }>;
  teamId?: string;
  houseId?: string;
  eventId?: string;
  media?: Array<{
    fileUrl: string;
    mimeType: string;
  }>;
}

export interface HouseTotalsRow {
  houseId: string;
  houseName: string;
  totalPoints: number;
  awardCount: number;
}

export interface AwardsSummary {
  totalPoints: number;
  studentCount: number;
  requiresFilters?: boolean;
}

export function getAwardAcademicYears(user: AppUser): Promise<AcademicYear[]> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/academicYears`, { headers: { usid: key } })
    .then(r => r.data);
}

export function getSchoolAwardTypes(
  user: AppUser,
  opts?: {
    where?: Record<string, any>;
    limit?: number;
    skip?: number;
  }
): Promise<AwardType[]> {
  const filter: any = {
    limit: opts?.limit ?? DEFAULT_LIMIT,
    skip: opts?.skip ?? DEFAULT_SKIP
  };
  if (opts?.where) {
    filter.where = opts.where;
  }

  return getAwardTypesList(user, filter) as Promise<AwardType[]>;
}

export function grantAward(user: AppUser, awardData: AwardEventInput): Promise<{ id: string }[]> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents`, awardData, {
      headers: { usid: key }
    })
    .then(r => r.data);
}

export function getAwardEventLogs(user: AppUser, awardEventId: string): Promise<AwardLogEntry[]> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/${awardEventId}/logs`, {
      headers: { usid: key }
    })
    .then(r => r.data);
}

export function getCurrentYearAwardEvents(
  user: AppUser,
  uiFilter?: { where?: any; limit?: number; skip?: number; order?: string }
): Promise<AwardEvent[]> {
  const { roleSessionKey: usid, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/current`, {
      headers: { usid },
      params: {
        filter: JSON.stringify(uiFilter || {})
      }
    })
    .then(r => r.data);
}

export function getCurrentYearAwardEventsCount(user: AppUser, where?: any): Promise<AwardsSummary & { count: number }> {
  const { roleSessionKey: usid, activeSchoolId } = user;
  const filterObj = where ? { where } : {};
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/current/count`, {
      headers: { usid },
      params: { filter: JSON.stringify(filterObj) }
    })
    .then(r => r.data);
}

export function getAllCurrentYearAwardEvents(user: AppUser, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: JSON.stringify({
          where: {
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        })
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: JSON.stringify({
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP
        })
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/current`, config).then(response => {
    return response.data;
  });
}

export function getArchivedAwardEvents(
  user: AppUser,
  uiFilter?: { where?: any; limit?: number; skip?: number; order?: string }
): Promise<AwardEvent[]> {
  const { roleSessionKey: usid, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/archive`, {
      headers: { usid },
      params: { filter: JSON.stringify(uiFilter || {}) }
    })
    .then(r => r.data);
}

export function getArchivedAwardEventsCount(user: AppUser, where?: any): Promise<AwardsSummary & { count: number }> {
  const { roleSessionKey: usid, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/archive/count`, {
      headers: { usid },
      params: { filter: JSON.stringify(where ? { where } : {}) }
    })
    .then(r => r.data);
}

export function updateAwardEvent(user: AppUser, data, awardEventId: string) {
  console.warn('[updateAwardEvent CALLED]', { awardEventId, data, stack: new Error().stack });

  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/${awardEventId}`, data, config)
    .then(response => {
      return response.data;
    });
}

export function deleteAwardEvent(user: AppUser, awardEventId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/${awardEventId}`, config)
    .then(response => {
      return response.data;
    });
}

export function getHouseTotals(user: AppUser, academicYearId?: string): Promise<HouseTotalsRow[]> {
  const { roleSessionKey: usid, activeSchoolId } = user;
  const where = academicYearId ? { academicYearId } : undefined;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardEvents/houseTotals`, {
      headers: { usid },
      params: { filter: JSON.stringify(where ? { where } : {}) }
    })
    .then(r => r.data);
}

export function getCurrentYearHouseTotals(user: AppUser): Promise<HouseTotalsRow[]> {
  return getHouseTotals(user);
}

export function getEventAwardEvents(
  user: AppUser,
  eventId: string,
  uiFilter?: { where?: any; limit?: number; skip?: number; order?: string }
): Promise<AwardEvent[]> {
  const { roleSessionKey: usid, activeSchoolId } = user;

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/events/${encodeURIComponent(eventId)}/awardEvents`, {
      headers: { usid },
      params: {
        filter: JSON.stringify(uiFilter || {})
      }
    })
    .then(r => r.data);
}
